import PropTypes from "prop-types";
import styled from "styled-components";
import { FaPrint, FaShare, FaExclamationTriangle } from "react-icons/fa";
import ContentLayout from "../../../layout/ContentLayout";
import { H2, H3, Paragraph } from "../../../styles/AppStyles";
import { LINKS, href } from "../../../config/links";

const CalloutBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  border-left: 5px solid ${({ $type, theme }) => {
    if ($type === "critical") return "#dc2626";
    if ($type === "do") return "#10b981";
    if ($type === "dont") return "#ef4444";
    if ($type === "warning") return "#f59e0b";
    if ($type === "tip") return "#6366f1";
    return theme.iconColor;
  }};
  background-color: ${({ $type, theme }) => {
    if ($type === "critical") return "rgba(220, 38, 38, 0.08)";
    if ($type === "do") return "rgba(16, 185, 129, 0.08)";
    if ($type === "dont") return "rgba(239, 68, 68, 0.08)";
    if ($type === "warning") return "rgba(245, 158, 11, 0.08)";
    if ($type === "tip") return "rgba(99, 102, 241, 0.08)";
    return "rgba(99, 102, 241, 0.08)";
  }};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  strong {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    color: ${({ $type }) => {
      if ($type === "critical") return "#dc2626";
      if ($type === "do") return "#10b981";
      if ($type === "dont") return "#ef4444";
      if ($type === "warning") return "#f59e0b";
      if ($type === "tip") return "#6366f1";
      return "#6366f1";
    }};
  }

  ul {
    margin: 0.75rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }
`;

const ChecklistSection = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.iconColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  h3 {
    margin-top: 0;
  }
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  input[type="checkbox"] {
    margin-top: 0.35rem;
    flex-shrink: 0;
    width: 1.1rem;
    height: 1.1rem;
    cursor: pointer;
  }

  label {
    flex: 1;
    cursor: pointer;
    line-height: 1.6;
  }
`;

const MalwareTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.25rem;
  margin: 1.5rem 0;
`;

const MalwareTypeCard = styled.div`
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.background};
  border: 2px solid ${({ theme }) => theme.iconColor};
  border-radius: 8px;
  color: ${({ theme }) => theme.text};

  h4 {
    margin-top: 0;
    color: ${({ theme }) => theme.iconColor};
  }

  p {
    font-size: 0.93rem;
    line-height: 1.55;
    margin: 0;
  }
`;

const ScenarioBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.background};
  border: 2px dashed ${({ theme }) => theme.iconColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  h4 {
    margin-top: 0;
    color: ${({ theme }) => theme.iconColor};
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .scenario-text {
    font-style: italic;
    margin: 0.75rem 0;
    padding: 0.75rem;
    background-color: ${({ theme }) =>
      theme.isdarkmode ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.05)"};
    border-radius: 4px;
  }

  .response {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid ${({ theme }) => theme.iconColor};

    strong {
      color: #10b981;
      display: block;
      margin-bottom: 0.5rem;
    }

    ul {
      margin: 0;
      padding-left: 1.5rem;

      li {
        margin-bottom: 0.5rem;
      }
    }
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;

    button,
    a {
      width: 100%;
    }
  }
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background-color: ${({ theme }) => theme.iconColor};
  color: ${({ theme }) => theme.background};
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const MalwareDetectionGuide = ({ istouch, isdarkmode }) => {
  const canonicalUrl = href(LINKS.QUICK_GUIDES.MALWARE_DETECTION);

  // const heroImageSrc = "/images/guides/malware-detection.webp";

  return (
    <ContentLayout
        istouch={istouch}
        isdarkmode={isdarkmode}
        displayTitle="Is My Device Infected? Malware Detection & Removal"
        seoTitle="Malware Detection Guide: Symptoms, Isolation & Removal | CSFSafe"
        canonicalUrl={canonicalUrl}
        description="Quick guide to recognizing malware infection symptoms, isolating an infected device safely, removing malware, and verifying the fix worked."
      ogTitle="Malware Detection Guide: Symptoms, Isolation & Removal | CSFSafe"
      ogDescription="Symptoms checklist, isolation steps, and when to call for professional help if you suspect a malware infection."
      // ogImage={heroImageSrc}
      // heroImageSrc={heroImageSrc}
      heroImageAlt="Malware detection and device isolation"
      publishedLabel="Quick Reference Guide — July 22, 2026"
      showCTA={true}
    >
      <Paragraph>
        A device acting strangely—running slow, popping up ads, or behaving in
        ways you don't recognize—may be infected with malware. This guide
        helps you recognize the signs, handle a suspected infection safely,
        know when to call for professional help, and verify the device is
        truly clean afterward.
      </Paragraph>

      <H2>Common Malware Types</H2>

      <MalwareTypeGrid>
        <MalwareTypeCard>
          <h4>Ransomware</h4>
          <p>
            Encrypts your files and demands payment for the decryption key.
            Often spreads across shared drives.
          </p>
        </MalwareTypeCard>
        <MalwareTypeCard>
          <h4>Spyware</h4>
          <p>
            Secretly monitors activity, captures keystrokes, and steals
            credentials or sensitive data without obvious symptoms.
          </p>
        </MalwareTypeCard>
        <MalwareTypeCard>
          <h4>Trojans</h4>
          <p>
            Disguised as legitimate software; once installed, opens a backdoor
            for attackers to access your system.
          </p>
        </MalwareTypeCard>
        <MalwareTypeCard>
          <h4>Adware</h4>
          <p>
            Floods your device with unwanted ads and pop-ups; often bundled
            with free downloads.
          </p>
        </MalwareTypeCard>
        <MalwareTypeCard>
          <h4>Worms</h4>
          <p>
            Self-replicating malware that spreads across networks without
            needing user action, often causing widespread slowdowns.
          </p>
        </MalwareTypeCard>
        <MalwareTypeCard>
          <h4>Cryptojacking</h4>
          <p>
            Secretly uses your device's processing power to mine
            cryptocurrency, causing overheating and severe slowdowns.
          </p>
        </MalwareTypeCard>
      </MalwareTypeGrid>

      <H2>Infection Indicators</H2>

      <H3>What to Watch For</H3>

      <ChecklistSection>
        <h3>Symptoms Checklist</h3>
        <ChecklistItem>
          <input type="checkbox" id="item1" />
          <label htmlFor="item1">
            Device is significantly slower than usual, even with few programs
            open
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item2" />
          <label htmlFor="item2">
            Unexpected pop-ups, ads, or browser toolbars you didn't install
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item3" />
          <label htmlFor="item3">
            Programs launching, crashing, or closing on their own
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item4" />
          <label htmlFor="item4">
            Unfamiliar programs, browser extensions, or icons appearing
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item5" />
          <label htmlFor="item5">
            Antivirus software disabled without your action, or unable to
            update
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item6" />
          <label htmlFor="item6">
            Files renamed, missing, or with unfamiliar extensions
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item7" />
          <label htmlFor="item7">
            Unusually high network activity or data usage when idle
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item8" />
          <label htmlFor="item8">
            Fan running constantly or device overheating even during light use
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item9" />
          <label htmlFor="item9">
            Friends/colleagues report receiving strange messages from your
            accounts
          </label>
        </ChecklistItem>
      </ChecklistSection>

      <H2>Safe Handling Procedures</H2>

      <H3>Isolate Before You Investigate</H3>

      <CalloutBox $type="critical">
        <strong>Isolation Steps: Do This First</strong>
        <ul>
          <li>
            Disconnect from the network—unplug Ethernet or turn off WiFi—to
            stop malware from spreading or communicating with attackers
          </li>
          <li>
            Don't shut down immediately if you suspect ransomware or active
            data theft—this can destroy evidence needed for investigation
          </li>
          <li>
            Don't plug the infected device into other systems or shared
            drives
          </li>
          <li>
            Avoid using the device for anything sensitive (banking, email
            login) until it's confirmed clean
          </li>
          <li>
            Notify your IT/security team before attempting removal on a work
            device
          </li>
        </ul>
      </CalloutBox>

      <H2>Removal Steps (For Non-Critical, Confirmed Malware)</H2>

      <CalloutBox $type="tip">
        <strong>Basic Removal Process</strong>
        <ul>
          <li>
            Boot into Safe Mode (Windows) or Safe Boot (Mac) so malware
            processes don't fully load
          </li>
          <li>
            Run a full system scan with updated antivirus/anti-malware
            software
          </li>
          <li>
            Use a second reputable scanner (e.g., Malwarebytes) as a
            cross-check—no single tool catches everything
          </li>
          <li>
            Remove or quarantine anything flagged; review browser extensions
            and startup programs for anything unfamiliar
          </li>
          <li>
            Change passwords for accounts accessed on the device—from a
            different, clean device
          </li>
          <li>
            Update the OS and all software to patch the vulnerability that
            allowed infection
          </li>
        </ul>
      </CalloutBox>

      <H2>When to Call for Professional Help</H2>

      <CalloutBox $type="warning">
        <strong>Don't DIY These Situations</strong>
        <ul>
          <li>Ransomware or any sign files have been encrypted</li>
          <li>The device is used to access sensitive company data, financial systems, or customer records</li>
          <li>Multiple devices on the network show signs of infection simultaneously</li>
          <li>Antivirus can't remove the threat, or it keeps reappearing after removal</li>
          <li>You suspect data was stolen, not just that the device is infected</li>
          <li>The device is a server, or infection could affect shared/network resources</li>
        </ul>
      </CalloutBox>

      <H2>Antivirus Basics</H2>

      <CalloutBox $type="do">
        <strong>Keep Protection Active</strong>
        <ul>
          <li>Use reputable antivirus/endpoint protection on every device—built-in options (Windows Defender, macOS XProtect) are a solid baseline</li>
          <li>Keep virus definitions updated automatically—outdated definitions miss new threats</li>
          <li>Schedule regular full-system scans, not just quick scans</li>
          <li>Never disable antivirus "to make something work faster"—if a legitimate program is being blocked, verify with IT before disabling protection</li>
          <li>Enable real-time protection, not just on-demand scanning</li>
        </ul>
      </CalloutBox>

      <H2>Post-Removal Verification</H2>

      <ChecklistSection>
        <h3>Confirm the Device Is Actually Clean</h3>
        <ChecklistItem>
          <input type="checkbox" id="item10" />
          <label htmlFor="item10">
            Run a second full scan after removal to confirm nothing remains
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item11" />
          <label htmlFor="item11">
            Monitor performance and behavior for several days after removal
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item12" />
          <label htmlFor="item12">
            Check for unfamiliar scheduled tasks, startup items, or new user
            accounts
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item13" />
          <label htmlFor="item13">
            Verify passwords for accounts accessed on the device have been
            changed
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item14" />
          <label htmlFor="item14">
            Confirm the initial infection vector (phishing email, malicious
            download, exposed remote access) is closed
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item15" />
          <label htmlFor="item15">
            If the device connects to company systems, notify IT so they can
            monitor for lateral spread
          </label>
        </ChecklistItem>
      </ChecklistSection>

      <H2>Real Incident Scenario</H2>

      <ScenarioBox>
        <h4>
          <FaExclamationTriangle /> The Slow Computer That Wasn't Just Slow
        </h4>
        <div className="scenario-text">
          An employee's laptop became noticeably sluggish over a week, with
          the fan running constantly even when idle. They assumed it needed a
          restart and kept working. By the time they reported it, task manager
          showed an unfamiliar process consuming 90% of CPU, and the antivirus
          icon had disappeared from the taskbar.
        </div>
        <div className="response">
          <strong>What Should Have Happened Sooner:</strong>
          <ul>
            <li>
              Report unusual slowness and antivirus being disabled to IT
              immediately, not after a week
            </li>
            <li>
              Disconnect the device from the network as soon as compromise was
              suspected
            </li>
            <li>
              Have IT investigate what disabled the antivirus—this is a
              classic cryptojacking/trojan behavior
            </li>
            <li>
              Check other devices on the same network for similar symptoms,
              since malware that disables security tools often spreads
              laterally
            </li>
          </ul>
        </div>
      </ScenarioBox>

      <H2>Related Resources</H2>
      <ul>
        <li>
          <a href={href(LINKS.THREAT_TYPE.MALWARE)}>
            Malware Threat Type: Full Article
          </a>
        </li>
        <li>
          <a href={href(LINKS.THREAT_TYPE.CRYPTOJACKING)}>
            Cryptojacking Threat Type
          </a>
        </li>
        <li>
          <a href={href(LINKS.QUICK_GUIDES.RANSOMWARE_RESPONSE)}>
            Ransomware Response Quick Guide
          </a>
        </li>
        <li>
          <a href={href(LINKS.QUICK_GUIDES.DEVICE_SECURITY)}>
            Harden Your Computer &amp; Mobile Device
          </a>
        </li>
      </ul>

      <ButtonRow data-print="hide">
        <Button
          as="button"
          type="button"
          onClick={() => window.print()}
          style={{ fontFamily: "inherit" }}
        >
          <FaPrint /> Print or save as PDF
        </Button>
        <Button
          href="https://www.facebook.com/sharer/sharer.php?u=https://csfsafe.com/resources/quick-guides/malware-detection"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaShare /> Share Guide
        </Button>
      </ButtonRow>
      </ContentLayout>
  );
};

MalwareDetectionGuide.propTypes = {
  istouch: PropTypes.bool.isRequired,
  isdarkmode: PropTypes.bool.isRequired,
};

export default MalwareDetectionGuide;
